import type { MouseEvent, TouchEvent } from "react";
import { useCallback, useRef, useState } from "react";

import { useIsomorphicLayoutEffect } from "./use-isomorphic-layout-effect";
import { usePrevious } from "./use-previous";

export interface UseLongPressParams {
  shouldPreventDefault?: boolean;
  delay?: number;
  onLongPress: () => void;
  onLongPressReleased?: () => void;
  onPress: () => void;
}

export function useLongPress({
  shouldPreventDefault = true,
  delay = 100,
  onLongPress,
  onLongPressReleased,
  onPress,
}: UseLongPressParams) {
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const timeout = useRef<NodeJS.Timeout>();
  const target = useRef<EventTarget>();

  const start = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (shouldPreventDefault && event.target) {
        event.target.addEventListener("touchend", preventDefault, {
          passive: false,
        });
        target.current = event.target;
      }
      timeout.current = setTimeout(() => {
        onLongPress();
        setLongPressTriggered(true);
      }, delay);
    },
    [onLongPress, delay, shouldPreventDefault],
  );

  const clear = useCallback(
    (_event: MouseEvent | TouchEvent, shouldTriggerClick = true) => {
      timeout.current && clearTimeout(timeout.current);

      shouldTriggerClick && !longPressTriggered && onPress();
      setLongPressTriggered(false);
      if (shouldPreventDefault && target.current) {
        target.current.removeEventListener("touchend", preventDefault);
      }
    },
    [shouldPreventDefault, onPress, longPressTriggered],
  );

  const prevLongPressTriggered = usePrevious(longPressTriggered);
  useIsomorphicLayoutEffect(() => {
    if (!longPressTriggered && prevLongPressTriggered) {
      onLongPressReleased?.();
    }
  }, [longPressTriggered, prevLongPressTriggered, onLongPressReleased]);

  return {
    onClear: () => setLongPressTriggered(false),
    onClick: (_e: MouseEvent) => {
      // console.debug("onClick")
    },
    onMouseDown: (e: MouseEvent) => {
      // console.debug("onMouseDown")
      if (e.button === 2) {
        e.preventDefault();
      } else {
        start(e);
      }
    },
    onTouchStart: (e: TouchEvent) => {
      // console.debug("onTouchStart")
      start(e);
    },
    onMouseUp: (e: MouseEvent) => {
      // console.debug("onMouseUp")
      if (e.button === 2) {
        e.preventDefault();
      } else {
        clear(e);
      }
    },
    onMouseLeave: (e: MouseEvent) => {
      // console.debug("onMouseLeave")
      if (e.button === 2) {
        e.preventDefault();
      } else {
        clear(e, false);
      }
    },
    onTouchEnd: (e: TouchEvent) => {
      // console.debug("onTouchEnd")
      clear(e);
    },
    onContextMenu: (e: MouseEvent) => {
      // console.debug("onContextMenu")
      e.preventDefault();
    },
  };
}

interface EventWithTouches extends Event {
  touches: unknown[];
}

const isTouchEvent = (event: Event): event is EventWithTouches => {
  return "touches" in event;
};

const preventDefault = (event: Event) => {
  if (!isTouchEvent(event)) return;

  if (event.touches.length < 2 && event.preventDefault) {
    event.preventDefault();
  }
};
